import { useCallback, type ChangeEvent } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  selectFileList,
  selectFileLoading,
  selectFileUpload,
  selectSelectedStore,
  selectStores,
} from "../redux/selectors";
import {
  createLocalStoreFileAction,
  deleteLocalStoreFileAction,
  getArtifactsFileActions,
} from "../redux/actions/local_store_actions";
import {
  createStoreFileAction,
  deleteStoreFileAction,
} from "../redux/actions/openai_store_actions";
import { getUpdatedStores } from "../helpers";
import { setSelectedStore, setStoreAction } from "../redux/slice";
import { generateId } from "../../../shared/utils/generateId";

export const useAddDatabaseFile = () => {
  const dispatch = useAppDispatch();
  const dataStore = useAppSelector(selectStores);
  const isFileUpload = useAppSelector(selectFileUpload);
  const selectedStore = useAppSelector(selectSelectedStore);
  const files = useAppSelector(selectFileList);
  const isFileLoading = useAppSelector(selectFileLoading);

  const handleCreateStoreFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = event?.target?.files;
      if (!fileList || fileList.length === 0 || !selectedStore?.id) return;

      const filesArray = Array.from(fileList);
      const newFiles = filesArray.map((file) => ({
        id: generateId(),
        filename: file.name,
        storeId: selectedStore.id,
        isLoading: true,
      }));
      const updates = getUpdatedStores(dataStore, selectedStore, {
        files: [...(selectedStore.files || []), ...newFiles],
      });
      dispatch(setStoreAction(updates.newStores));
      dispatch(setSelectedStore(updates.newStore));

      await Promise.all(
        newFiles.map((newFile, index) => {
          const file = filesArray[index];
          if (selectedStore?.metadata.type === "local") {
            return dispatch(
              createLocalStoreFileAction({
                file,
                storeId: selectedStore.id,
                tempFileId: newFile.id,
              }),
            ).unwrap();
          } else {
            return dispatch(
              createStoreFileAction({
                file,
                storeId: selectedStore.id,
                tempFileId: newFile.id,
              }),
            ).unwrap();
          }
        }),
      );
    },
    [selectedStore, dataStore, files, dispatch],
  );
  const handleClick = async (files: { hash: string; filename: string }[]) => {
    if (!files || files.length === 0 || !selectedStore?.id) return;
    const filesArray = await Promise.all(
      files.map(async (file) => {
        const content = await dispatch(
          getArtifactsFileActions({
            hash: file.hash,
            fileName: file.filename,
          }),
        ).unwrap();
        return content;
      }),
    );

    const newFiles = filesArray.map((file) => ({
      id: generateId(),
      filename: file.name,
      storeId: selectedStore.id,
      isLoading: true,
    }));

    const updates = getUpdatedStores(dataStore, selectedStore, {
      files: [...(selectedStore.files || []), ...newFiles],
    });
    dispatch(setStoreAction(updates.newStores));
    dispatch(setSelectedStore(updates.newStore));

    await Promise.all(
      newFiles.map((newFile, index) => {
        const file = filesArray[index];
        if (selectedStore.metadata.type === "local") {
          return dispatch(
            createLocalStoreFileAction({
              file,
              storeId: selectedStore.id,
              tempFileId: newFile.id,
            }),
          ).unwrap();
        } else {
          return dispatch(
            createStoreFileAction({
              file,
              storeId: selectedStore.id,
              tempFileId: newFile.id,
            }),
          ).unwrap();
        }
      }),
    );
  };

  const deleteFile = useCallback(
    (id: string) => {
      if (selectedStore?.metadata.type === "local") {
        dispatch(deleteLocalStoreFileAction(id));
      } else {
        dispatch(deleteStoreFileAction(id));
      }
    },
    [selectedStore, files],
  );

  return {
    isFileUpload,
    isFileLoading,
    handleClick,
    handleCreateStoreFile,
    deleteFile,
  };
};
